import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { LineDiv, SectionDiv, Text } from "../design";
import ConstructionSiteApi from "../../API/constructionSiteApi";
import StatisticApi from "../../API/StatisticApi";

const Heading = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
    color: #17683a;
    font-size: 13px;
    font-weight: 700;
`;

const Tag = styled.span`
    padding: 2px 6px;
    background: #dcfce7;
    border-radius: 5px;
    color: #166534;
    font-size: 11px;
    font-weight: 700;
`;

const PreviousPercent = styled.span`
    padding: 2px 6px;
    border-radius: 5px;
    background: #fef2f2;
    color: #b91c1c;
    font-size: 11px;
    font-weight: 600;
`;

const PercentButton = styled(LineDiv)`
    min-width: 52px;
    font-variant-numeric: tabular-nums;
    color: ${({ $selected }) => ($selected ? "#08783b" : "#374151")};
    font-weight: ${({ $selected }) => ($selected ? 700 : 500)};
`;

const EmptyText = styled.div`
    width: 100%;
    padding: 8px;
    text-align: center;
    color: #64748b;
    font-size: 12px;
`;

const PERCENT_OPTIONS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

const ConstructionSiteProgress = ({
                                      Data,
                                      setData,
                                      selectedHouse,
                                      selectedFloor
                                  }) => {
    const [serverData, setServerData] = useState({});
    const [progressWorkTypes, setProgressWorkTypes] = useState([]);
    const [selectedType, setSelectedType] = useState(null);
    const [selectedWork, setSelectedWork] = useState(null);
    const [loadError, setLoadError] = useState("");

    const floorData = Data?.[selectedHouse]?.Floors?.[selectedFloor];
    const savedTypes = floorData?.ProgressWorkTypes ?? {};

    const availableWorks = useMemo(() => {
        return progressWorkTypes.find(
            (item) => item.WorkTypeName === selectedType?.WorkTypeName
        )?.ProgressWorks ?? [];
    }, [progressWorkTypes, selectedType]);

    const currentWorks = selectedType
        ? savedTypes[selectedType.WorkTypeName]?.ProgressWorks ?? []
        : [];

    const selectedWorkData = selectedWork
        ? currentWorks.find(
            (item) => String(item.ProgressWorkId) === String(selectedWork.ProgressWorkId)
        )
        : null;

    // Обновляем данные без мутаций и сразу сохраняем локальную копию.
    const updateLocalData = (update) => {
        if (!selectedHouse || !selectedFloor) return;

        const next = structuredClone(Data);
        const floor = next[selectedHouse]?.Floors?.[selectedFloor];
        if (!floor) return;
        floor.ProgressWorkTypes ??= {};

        update(floor.ProgressWorkTypes);
        next.status = "local";
        localStorage.setItem("Data", JSON.stringify(next));
        setData(next);
    };

    useEffect(() => {
        let cancelled = false;
        ConstructionSiteApi.getAllProgressWorkTypes()
            .then((result) => {
                if (cancelled) return;
                if (!result?.Status) throw new Error("Unable to load work types");
                const types = Object.entries(result.ProgressWorkTypes ?? {}).map(
                    ([name, value]) => ({
                        WorkTypeName: name,
                        WorkTypeId: value.WorkTypeId,
                        ProgressWorks: value.ProgressWorks ?? []
                    })
                );
                setProgressWorkTypes(types);
                setLoadError("");
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error(error);
                    setLoadError("Не удалось загрузить виды работ");
                }
            });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        setSelectedType(null);
        setSelectedWork(null);
    }, [selectedHouse, selectedFloor]);

    // Последние известные значения работ из сохранённых обходов.
    useEffect(() => {
        let cancelled = false;
        StatisticApi.getGoAroundData()
            .then((result) => {
                if (cancelled) return;
                const grouped = {};
                const records = Array.isArray(result?.Data) ? [...result.Data] : [];
                records.sort((a, b) =>
                    String(a.go_around_date ?? "").localeCompare(
                        String(b.go_around_date ?? "")
                    )
                );

                for (const record of records) {
                    for (const [house, houseData] of Object.entries(record.go_around_data ?? {})) {
                        for (const [floor, floorValue] of Object.entries(houseData?.Floors ?? {})) {
                            for (const [type, typeData] of Object.entries(floorValue?.ProgressWorkTypes ?? {})) {
                                for (const work of typeData?.ProgressWorks ?? []) {
                                    grouped[house] ??= {};
                                    grouped[house][floor] ??= {};
                                    grouped[house][floor][type] ??= {};
                                    grouped[house][floor][type][work.ProgressWorkName] = work.Percent;
                                }
                            }
                        }
                    }
                }
                setServerData(grouped);
            })
            .catch((error) => console.error("History load error:", error));
        return () => { cancelled = true; };
    }, []);

    function selectType(type) {
        const isSame = selectedType?.WorkTypeName === type.WorkTypeName;

        // Удаляем пустой тип при уходе с него.
        if (selectedType) {
            updateLocalData((types) => {
                const old = types[selectedType.WorkTypeName];
                if (old && old.ProgressWorks.length === 0) {
                    delete types[selectedType.WorkTypeName];
                }
            });
        }

        setSelectedType(isSame ? null : type);
        setSelectedWork(null);
    }

    function selectWork(work) {
        const isSame = selectedWork?.ProgressWorkId === work.ProgressWorkId;
        if (isSame) {
            setSelectedWork(null);
            return;
        }

        updateLocalData((types) => {
            const typeName = selectedType.WorkTypeName;
            types[typeName] ??= {
                ProgressWorkTypeId: selectedType.WorkTypeId,
                ProgressWorks: []
            };
            const works = types[typeName].ProgressWorks;
            const exists = works.some(
                (item) => String(item.ProgressWorkId) === String(work.ProgressWorkId)
            );
            if (!exists) {
                works.push({
                    ProgressWorkId: work.ProgressWorkId,
                    ProgressWorkName: work.ProgressWorkName,
                    Percent: 0
                });
            }
        });
        setSelectedWork(work);
    }

    function selectPercent(percent) {
        if (!selectedType || !selectedWork) return;
        updateLocalData((types) => {
            const typeName = selectedType.WorkTypeName;
            const type = types[typeName];
            if (!type) return;
            const index = type.ProgressWorks.findIndex(
                (item) => String(item.ProgressWorkId) === String(selectedWork.ProgressWorkId)
            );
            if (index < 0) return;
            if (percent === 0) {
                type.ProgressWorks.splice(index, 1);
                if (type.ProgressWorks.length === 0) delete types[typeName];
            } else {
                type.ProgressWorks[index].Percent = percent;
            }
        });
        if (percent === 0) setSelectedWork(null);
    }

    if (!selectedHouse || !selectedFloor) return null;

    return (
        <>
            <SectionDiv $visible $clickable>
                <Heading>1. Gewerk auswählen</Heading>
                {loadError && <EmptyText>{loadError}</EmptyText>}
                {progressWorkTypes.map((type) => {
                    const active = selectedType?.WorkTypeName === type.WorkTypeName;
                    const saved = (savedTypes[type.WorkTypeName]?.ProgressWorks?.length ?? 0) > 0;
                    return (
                        <LineDiv
                            as="button"
                            type="button"
                            key={type.WorkTypeId}
                            $selected={active}
                            aria-pressed={active}
                            onClick={() => selectType(type)}
                        >
                            <Text>{type.WorkTypeName}</Text>
                            {saved && <Tag>✓</Tag>}
                        </LineDiv>
                    );
                })}
            </SectionDiv>

            <SectionDiv $visible={Boolean(selectedType)} $clickable={Boolean(selectedType)}>
                <Heading>2. Arbeit auswählen</Heading>
                {availableWorks.length === 0 && <EmptyText>Keine Arbeiten vorhanden</EmptyText>}
                {availableWorks.map((work) => {
                    const active = selectedWork?.ProgressWorkId === work.ProgressWorkId;
                    const entry = currentWorks.find(
                        (item) => String(item.ProgressWorkId) === String(work.ProgressWorkId)
                    );
                    const previous = serverData?.[selectedHouse]?.[selectedFloor]?.[selectedType.WorkTypeName]?.[work.ProgressWorkName];
                    return (
                        <LineDiv
                            as="button"
                            type="button"
                            key={work.ProgressWorkId}
                            $selected={active}
                            aria-pressed={active}
                            onClick={() => selectWork(work)}
                        >
                            <Text>{work.ProgressWorkName}</Text>
                            {entry && Number(entry.Percent) > 0 && <Tag>{entry.Percent}%</Tag>}
                            {previous !== undefined && previous !== null && (
                                <PreviousPercent title="Zuletzt gespeicherter Fortschritt">
                                    {previous}%
                                </PreviousPercent>
                            )}
                        </LineDiv>
                    );
                })}
            </SectionDiv>

            <SectionDiv $visible={Boolean(selectedWork)} $clickable={Boolean(selectedWork)}>
                <Heading>3. Fortschritt festlegen</Heading>
                {PERCENT_OPTIONS.map((percent) => {
                    const active = Number(selectedWorkData?.Percent) === percent;
                    return (
                        <PercentButton
                            as="button"
                            type="button"
                            key={percent}
                            $selected={active}
                            aria-pressed={active}
                            onClick={() => selectPercent(percent)}
                        >
                            {percent}%
                        </PercentButton>
                    );
                })}
            </SectionDiv>
        </>
    );
};

export default ConstructionSiteProgress;
